import { useCallback, useMemo } from 'react';

import { getToolDefaultAssetType } from 'src/data/generationTool';
import { SCORING_METHOD_TYPE } from 'src/data/scoring';
import * as events from 'src/lib/events';
import { PAGES, SUB_PAGES, useTrackingEvent } from 'src/services/useTrackingEvent';
import { useCustomerHasLiveScoring } from 'src/store';

import { useDocumentViewActions, useMainActions } from '../store';

import { useGetProjectAttributes } from './useGetProjectAttributes';

export const useDocumentViewEvents = () => {
  const trackingEvent = useTrackingEvent();
  const getProjectAttributes = useGetProjectAttributes();
  const customerHasLiveScoring = useCustomerHasLiveScoring();
  const mainActions = useMainActions();
  const documentViewActions = useDocumentViewActions();

  const trackLockedGetScoreClicked = useCallback(
    ({ source, blockedReason }) => {
      const attributes = {
        ...getProjectAttributes(),
        liveScoringAccessible: customerHasLiveScoring,
        page: PAGES.editor,
        subPage: SUB_PAGES.generationPanel,
        generationTool: mainActions.getGenerationTool(),
        source,
        blockedReason,
      };

      trackingEvent(events.PROJECT.clickedLockedGetScore, attributes);
    },
    [trackingEvent, getProjectAttributes, customerHasLiveScoring, mainActions]
  );

  const trackLockedUndoClicked = useCallback(
    (source) => {
      const attributes = {
        ...getProjectAttributes(),
        liveScoringAccessible: customerHasLiveScoring,
        page: PAGES.editor,
        source,
      };

      trackingEvent(events.PROJECT.clickedLockedUndo, attributes);
    },
    [trackingEvent, getProjectAttributes, customerHasLiveScoring]
  );

  const trackLockedUpgradeToDDClicked = useCallback(
    (source) => {
      const attributes = {
        ...getProjectAttributes(),
        source,
        generationTool: mainActions.getGenerationTool(),
        page: PAGES.editor,
      };

      trackingEvent(events.PROJECT.clickedLockedUpgradeToDD, attributes);
    },
    [trackingEvent, getProjectAttributes, mainActions]
  );

  const trackShareClicked = useCallback(() => {
    const attributes = {
      ...getProjectAttributes(),
      page: PAGES.editor,
      subPage: SUB_PAGES.listView,
    };

    trackingEvent(events.DOCUMENT.shareClicked, attributes);
  }, [trackingEvent, getProjectAttributes]);

  const trackScoreDatasetLinkClick = useCallback(
    ({ generationTool, variationId }) => {
      const attributes = {
        ...getProjectAttributes(),
        assetType: getToolDefaultAssetType(generationTool),
        page: PAGES.editor,
        subPage: SUB_PAGES.insightsPanel,
        variationId,
      };

      trackingEvent(events.PROJECT.clickedScoreDatasetLink, attributes);
    },
    [trackingEvent, getProjectAttributes]
  );

  const trackScoreDatasetApply = useCallback(() => {
    // track only 'customModel' scoring method
    const scoringMethod = documentViewActions.getScoringMethod();
    if (scoringMethod?.type !== SCORING_METHOD_TYPE.customModel) {
      return;
    }

    const attributes = {
      ...getProjectAttributes(),
      page: PAGES.editor,
      subPage: SUB_PAGES.insightsPanel,
    };
    trackingEvent(events.PROJECT.applyScoreCustomModel, attributes);
  }, [trackingEvent, getProjectAttributes, documentViewActions]);

  const trackBrandTermApplied = useCallback(
    (detectedBrandTerm, variationId = null) => {
      const attributes = {
        ...getProjectAttributes(),
        variationId,
        brandTermId: detectedBrandTerm?.brandTerm?.id,
        originalText: detectedBrandTerm?.originalText,
        termInText: detectedBrandTerm?.termInText,
        replaceWith: detectedBrandTerm?.replaceWith,
        page: PAGES.brandTerms,
      };

      trackingEvent(events.BRAND_VOCABULARY.detectedTermApplied, attributes);
    },
    [trackingEvent, getProjectAttributes]
  );

  const trackBriefImportedFromUrl = useCallback(
    (generationTool) => {
      trackingEvent(events.DOCUMENT.briefImportedFromUrl, {
        ...getProjectAttributes(),
        generationTool,
        page: PAGES.editor,
      });
    },
    [trackingEvent, getProjectAttributes]
  );

  const trackBrandBriefLoaded = useCallback(() => {
    trackingEvent(events.DOCUMENT.brandBriefLoaded, {
      ...getProjectAttributes(),
      generationTool: mainActions.getGenerationTool(),
      page: PAGES.editor,
    });
  }, [trackingEvent, getProjectAttributes, mainActions]);

  const trackDisplayModeSwitched = useCallback(
    (mode) => {
      trackingEvent(events.DOCUMENT.displayModeToggle, {
        ...getProjectAttributes(),
        page: PAGES.editor,
        mode,
      });
    },
    [trackingEvent, getProjectAttributes]
  );

  return useMemo(
    () => ({
      trackLockedGetScoreClicked,
      trackLockedUndoClicked,
      trackLockedUpgradeToDDClicked,
      trackShareClicked,
      trackScoreDatasetLinkClick,
      trackScoreDatasetApply,
      trackBrandTermApplied,
      trackBriefImportedFromUrl,
      trackBrandBriefLoaded,
      trackDisplayModeSwitched,
    }),
    [
      trackLockedGetScoreClicked,
      trackLockedUndoClicked,
      trackLockedUpgradeToDDClicked,
      trackShareClicked,
      trackScoreDatasetLinkClick,
      trackScoreDatasetApply,
      trackBrandTermApplied,
      trackBriefImportedFromUrl,
      trackBrandBriefLoaded,
      trackDisplayModeSwitched,
    ]
  );
};
